import { DataTable } from "../../ui/DataTable";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { formDataToRow } from "./utils";
import {
  _ACTIONS,
  _CO,
  _COLORS,
  _HTTP,
  _INPUT_SALE_REGROUPING,
  _MESSAGES,
  _SALE_INPUTS,
  _SWAL_ICONS,
  _TAB_INPUT_SALES,
  _INPUT_SALES_OTHERS,
  _INPUT_SALE_DEPOTS,
} from "../../../utils/constants";
import { _ROUTES } from "../../../utils/_routes_constants";
import { RequestHelper } from "../../../utils/requestHelper";
import { createToast } from "../../../utils/toast";
import { columnBuilderVolume } from "./columnBuilderVolume";
import { defaultExcelStyle } from "../../../utils/AgGridUtils/defaultExcelStyle";
import { DataService } from "../../../services/DataService";
import { HandleValueCell } from "./HandleValueCell";

const TableInputSaleAggVolume = ({
  params,
  setRows,
  rows,
  tab,
  siteData,
  priceId,
  gridRef,
}) => {
  const dataService = new DataService();
  const requestHelper = new RequestHelper();
  const [columns, setColumns] = useState();
  const [refreshData, setRefreshData] = useState(false);
  const columnsId = useRef(new Array(12).fill(null));
  const prevInternalVolumeRef = useRef();
  const prevExternalVolumeRef = useRef();
  const discountIncreases = useRef([]);
  const creditsExternatCredit = useRef([]);
  const valuesForVolumes = useRef([]);

  const handleCellValueChanged = HandleValueCell({
    params,
    priceId,
    columnsId,
    valuesForVolumes,
    setRefreshData,
    rows,
    setRows,
  });

  const calculTotalVolume = (clonedRows) => {
    const types = [
      _INPUT_SALE_REGROUPING.EXTERNAL_CASH_GROUPING,
      _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING,
      _INPUT_SALE_REGROUPING.BOLCK_GROUPING,
      _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
    ];

    const inputs = [
      _SALE_INPUTS.aggregates,
      _SALE_INPUTS.crusherRun,
      _SALE_INPUTS.rocksand,
      _SALE_INPUTS.spallsFilterBoulders,
      _INPUT_SALES_OTHERS.INTERNAL_BLOCK,
      _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
    ];
    const rowTotal = clonedRows?.find(
      (row) => row?.sale === _INPUT_SALES_OTHERS.TOTAL_EXTERNAL_INTERNAL,
    );

    if (rowTotal !== undefined) {
      const months = Object.keys(_CO);
      months.forEach((month) => {
        if (tab === "volume") {
          rowTotal.months[month] = clonedRows.reduce((acc, row) => {
            if (types.includes(row.type)) {
              if (inputs.includes(row.sale)) {
                acc += row.months[month] !== undefined ? row.months[month] : 0;
              }
            }

            return acc;
          }, 0);
        }
      });
    }
  };

  /**
   * Calculates the external credit for each sale input and updates the corresponding row's monthly values.
   *
   * The function iterates over each sale input and finds the corresponding row with the type
   * `_INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING`. For each month, it sums up the monthly values
   * of rows that match the sale input and belong to specific types (`PMX_GROUPING`, `DMX_GROUPING`,
   * `GCC_GROUPING`, `OTHER_GROUPING`), and updates the external credit row's monthly values.
   *
   * @function
   * @name calculExternalCredit
   */
  const calculExternalCredit = (clonedRows) => {
    const types = [
      _INPUT_SALE_REGROUPING.PMX_GROUPING,
      _INPUT_SALE_REGROUPING.DMX_GROUPING,
      _INPUT_SALE_REGROUPING.GCC_GROUPING,
      _INPUT_SALE_REGROUPING.OTHER_GROUPING,
    ];

    const months = Object.keys(_CO);
    Object.entries(_SALE_INPUTS).forEach(([key, input]) => {
      const line = clonedRows?.find(
        (row) =>
          row?.sale === input &&
          row?.type === _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING,
      );
      if (line !== undefined) {
        months.forEach((month) => {
          line.credit = creditsExternatCredit?.current[line.sale];
          line.months[month] = clonedRows.reduce((acc, row) => {
            if (row.sale === input && types.includes(row.type)) {
              acc += row.months[month] !== undefined ? row.months[month] : 0;
            }

            return acc;
          }, 0);
        });
      }
    });
  };

  /**
   * Calculates the total external volume for each month and updates the corresponding row.
   *
   * The function iterates through the rows and sums up the volumes for specific types and inputs
   * for each month. The result is then assigned to the "EXTERNAL VOLUMES" row.
   *
   * Types considered:
   * - EXTERNAL_CASH_GROUPING
   * - EXTERNAL_CREDIT_GROUPING
   * - UNSECURED_GROUPING
   *
   * Inputs considered:
   * - aggregates
   * - crusherRun
   * - rocksand
   * - spallsFilterBoulders
   * - "UNSECURED CREDIT"
   *
   * The function assumes that the `rows` and `_CO` variables are available in the scope.
   */
  const calculTotalExternalVolume = (clonedRows) => {
    const types = [
      _INPUT_SALE_REGROUPING.EXTERNAL_CASH_GROUPING,
      _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING,
      _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
    ];

    const inputs = [
      _SALE_INPUTS.aggregates,
      _SALE_INPUTS.crusherRun,
      _SALE_INPUTS.rocksand,
      _SALE_INPUTS.spallsFilterBoulders,
      _INPUT_SALE_REGROUPING.UNSECURED_GROUPING,
    ];
    // pour ces sites on prend en compte les volumes des depots
    const siteqAddDepots = ["SM", "TK", "FA"];
    const siteCode = siteData?.find((site) => site.id === params.site)?.code;
    if (siteqAddDepots.includes(siteCode)) {
      types.push(_INPUT_SALE_REGROUPING.DEPOT_GROUPING);
      inputs.push(_INPUT_SALE_DEPOTS.VALENTINA, _INPUT_SALE_DEPOTS.TERRE_ROUGE);
    }

    const rowTotal = clonedRows?.find(
      (row) => row?.sale === _INPUT_SALES_OTHERS.EXTERNAL_VOLUME,
    );

    if (rowTotal !== undefined) {
      const months = Object.keys(_CO);
      months.forEach((month) => {
        rowTotal.months[month] = clonedRows.reduce((acc, row) => {
          if (types.includes(row.type)) {
            if (inputs.includes(row.sale)) {
              acc += row.months[month] !== undefined ? row.months[month] : 0;
            }
          }

          return acc;
        }, 0);
      });
    }
  };

  /**
   * Calculates the total internal volume for specific sales types and updates the corresponding row.
   *
   * The function iterates through each month and sums up the volumes for rows that match the specified types and sales.
   * The result is then assigned to the corresponding month in the "INTERNAL VOLUMES" row.
   *
   * @function
   * @name calcuTotalInternalVolume
   * @returns {void}
   */
  const calcuTotalInternalVolume = (clonedRows) => {
    const types = [
      _INPUT_SALE_REGROUPING.BOLCK_GROUPING,
      _INPUT_SALE_REGROUPING.DEPOT_GROUPING,
    ];

    const inputs = [_INPUT_SALES_OTHERS.INTERNAL_BLOCK];

    // for these sites, depot volumes are not taken into account
    const siteqAddDepots = ["SM", "TK", "FA"];
    const siteCode = siteData?.find((site) => site.id === params.site)?.code;
    if (!siteqAddDepots.includes(siteCode)) {
      types.push(_INPUT_SALE_REGROUPING.DEPOT_GROUPING);
      inputs.push(_INPUT_SALE_DEPOTS.VALENTINA, _INPUT_SALE_DEPOTS.TERRE_ROUGE);
    }

    const rowTotal = clonedRows?.find(
      (row) => row?.sale === _INPUT_SALES_OTHERS.INTERNAL_VOLUME,
    );

    if (rowTotal !== undefined) {
      const months = Object.keys(_CO);
      months.forEach((month) => {
        rowTotal.months[month] = clonedRows.reduce((acc, row) => {
          if (types.includes(row.type)) {
            if (inputs.includes(row.sale)) {
              acc += row.months[month] !== undefined ? row.months[month] : 0;
            }
          }

          return acc;
        }, 0);
      });
    }
  };

  /**
   * Calculates the credit for GCC or other customers based on the provided rows data.
   *
   * This function iterates over specific types and inputs to find matching rows and
   * calculates the credit for each row. The credit is calculated using the cash value,
   * credit discount, and discount percentage.
   *
   * @function
   * @returns {void}
   */
  const calculCreditGccOrOthersCustumer = (clonedRows) => {
    // discount credit
    const ligneCreditDiscount = clonedRows?.find(
      (row) =>
        row?.sale === _INPUT_SALES_OTHERS.CREDIT_DISCOUNT &&
        row?.type === _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_DISCOUNT_GROUPING,
    );
    const creditDiscount = ligneCreditDiscount?.credit;

    const types = [
      _INPUT_SALE_REGROUPING.GCC_GROUPING,
      _INPUT_SALE_REGROUPING.OTHER_GROUPING,
    ];
    const inputs = [
      _SALE_INPUTS.aggregates,
      _SALE_INPUTS.crusherRun,
      _SALE_INPUTS.rocksand,
      _SALE_INPUTS.spallsFilterBoulders,
    ];

    types.forEach((type) => {
      inputs.forEach((input) => {
        const ligneAggregateCashGoupExternalCash = clonedRows?.find(
          (row) =>
            row?.sale === input &&
            row?.type === _INPUT_SALE_REGROUPING.EXTERNAL_CASH_GROUPING,
        );
        const cash = ligneAggregateCashGoupExternalCash?.cash;

        const line = clonedRows?.find(
          (row) => row?.sale === input && row?.type === type,
        );
        if (line === undefined) return;
        const discount = line.discount;
        const credit = (
          parseFloat(cash) *
          (1 + creditDiscount * 0.01) *
          (1 - discount * 0.01)
        ).toFixed(2);

        line.credit = parseFloat(credit);
      });
    });
  };

  /**
   * Calculates and updates the credit value for the unsecured credit row in the dataset.
   *
   * The function performs the following steps:
   * 1. Finds the row corresponding to unsecured credit.
   * 2. Retrieves the credit discount value for external credit discount grouping.
   * 3. Calculates the total and average external cash for rows with external cash grouping.
   * 4. Updates the credit value for the unsecured credit row based on the calculated average external cash,
   *    credit discount, and the unsecured credit's discount value.
   *
   * If the unsecured credit row or required data is not found, the function exits early.
   *
   * @returns {void} This function does not return a value. It updates the `credit` property of the unsecured credit row directly.
   */
  const calculCreditUnsecured = (clonedRows) => {
    let nb = 0;
    const rowUnsecured = clonedRows?.find(
      (row) => row?.sale === _INPUT_SALES_OTHERS.UNSECURED_CREDIT,
    );
    if (!rowUnsecured) return;
    const rowUnsecuredDiscount = rowUnsecured.discount || 0;

    const creditDiscount =
      clonedRows?.find(
        (row) =>
          row?.sale === _INPUT_SALES_OTHERS.CREDIT_DISCOUNT &&
          row?.type ===
            _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_DISCOUNT_GROUPING,
      )?.credit || 0;

    const totalExternalCash = clonedRows?.reduce((acc, row) => {
      if (row.cash !== undefined && row.cash !== null) {
        if (parseFloat(row.cash) !== 0) {
          nb++;
          acc += parseFloat(row.cash);
        }
      }
      return acc;
    }, 0);

    const averageExternalCash = nb !== 0 ? totalExternalCash / nb : 0;

    rowUnsecured.credit =
      rowUnsecured?.discount !== undefined
        ? (
            averageExternalCash *
            (1 + creditDiscount * 0.01) *
            (1 - rowUnsecuredDiscount * 0.01)
          ).toFixed(2)
        : null;
  };

  // Calcul des données avec totaux pour l'affichage
  const computedRows = useMemo(() => {
    if (rows === undefined || rows.length === 0) return [];

    // Copie profonde pour éviter de muter les données originales
    const clonedRows = rows.map((row) => ({
      ...row,
      months: { ...row.months },
    }));

    calculExternalCredit(clonedRows);
    calculTotalVolume(clonedRows);
    calculTotalExternalVolume(clonedRows);
    calcuTotalInternalVolume(clonedRows);
    calculCreditGccOrOthersCustumer(clonedRows);
    calculCreditUnsecured(clonedRows);

    return clonedRows;
  }, [rows, refreshData, siteData, params.site, tab]);

  // get input increase
  useEffect(() => {
    if (tab === _TAB_INPUT_SALES.VOLUME) return;
    dataService
      .fetchData(
        _ROUTES.INPUT_RATE_INCREASE,
        {
          fiscalYear: params.fiscalYear,
          version: params.version,
          site: params.site,
        },
        (data) => {
          data.forEach((item) => {
            discountIncreases.current[item.fiscalMonth] = item.pcIncrease;
          });
        },
        { errorMessage: "Unable to retrieve discount increase" },
      )
      .catch(() => {
        createToast("error", _MESSAGES.error);
      });
  }, [tab]);

  useEffect(() => {
    setColumns(columnBuilderVolume([], tab, discountIncreases.current));
  }, [params, refreshData]);

  useEffect(() => {
    prevInternalVolumeRef.current = undefined;
    prevExternalVolumeRef.current = undefined;
    priceId.current = undefined;
    dataService
      .fetchData(
        _ROUTES.IPT_SALE_AGG_MONTHS,
        { ...params },
        (inputMonths) => {
          dataService
            .fetchData(
              _ROUTES.IPT_SALE_AGG_PRICES,
              { ...params },
              (inputPrices) => {
                dataService
                  .fetchData(
                    _ROUTES.IPT_SALE_DEPOT_PRODUCION_SPLIT,
                    {
                      fiscalYear: params.fiscalYear,
                      version: params.version,
                      site: siteData?.find((site) => site.code === "VA")?.id,
                      childrenSiteAGG: params.site,
                    },
                    (volumeValentina) => {
                      dataService
                        .fetchData(
                          _ROUTES.IPT_SALE_DEPOT_PRODUCION_SPLIT,
                          {
                            fiscalYear: params.fiscalYear,
                            version: params.version,
                            site: siteData?.find((site) => site.code === "TR")
                              ?.id,
                            childrenSiteAGG: params.site,
                          },
                          (volumeTerreRouge) => {
                            const rowData = formDataToRow(
                              inputMonths,
                              inputPrices,
                              volumeValentina,
                              volumeTerreRouge,
                              params.site,
                              tab,
                            );
                            setRows(rowData);
                            const rowsId = rowData[0]?.inputsId;
                            priceId.current = rowData[0]?.priceId;
                            columnsId.current = rowsId;
                          },
                          {
                            errorMessage:
                              "Unable to retrieve Terre Rouge Volume rows",
                          },
                        )
                        .catch(() => {
                          createToast("error", _MESSAGES.error);
                        });
                    },
                    {
                      errorMessage: "Unable to retrieve Valentina Volume rows",
                    },
                  )
                  .catch(() => {
                    createToast("error", _MESSAGES.error);
                  });
              },
              { errorMessage: "Unable to retrieve input sale prices rows" },
            )
            .catch(() => {
              createToast("error", _MESSAGES.error);
            });
        },
        { errorMessage: "Unable to retrieve input sale volume rows" },
      )
      .catch(() => {
        createToast("error", _MESSAGES.error);
      });
  }, [params]);

  const getRowStyle = (params) => {
    const { key, footer, aggData } = params.node;
    const isExternalCash =
      key === _INPUT_SALE_REGROUPING.EXTERNAL_CASH_GROUPING;
    const isExternalCredit =
      key === _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING;
    const isCreditPmx = key === _INPUT_SALE_REGROUPING.PMX_GROUPING;
    const isCreditDmx = key === _INPUT_SALE_REGROUPING.DMX_GROUPING;
    const isCreditGcc = key === _INPUT_SALE_REGROUPING.GCC_GROUPING;
    const isCreditOthers = key === _INPUT_SALE_REGROUPING.OTHER_GROUPING;
    const isUnsecured = key === _INPUT_SALE_REGROUPING.UNSECURED_GROUPING;
    const isDepots = key === _INPUT_SALE_REGROUPING.DEPOT_GROUPING;
    const isVolumes = key === _INPUT_SALE_REGROUPING.VOLUMES_GROUPING;
    const isTurnover = key === _INPUT_SALE_REGROUPING.TURN_GROUPING;
    if (
      isExternalCash ||
      isExternalCredit ||
      isCreditPmx ||
      isCreditDmx ||
      isCreditGcc ||
      isCreditOthers ||
      isUnsecured ||
      isDepots | isVolumes ||
      isTurnover
    ) {
      if (footer === undefined) {
        return {
          fontWeight: "bold",
          backgroundColor: _COLORS.midGrey,
        };
      } else {
        aggData.sale = isExternalCash
          ? "Cash"
          : isExternalCredit
            ? "Credit"
            : isCreditPmx
              ? "Credit PMX"
              : isCreditDmx
                ? "Credit DMX"
                : isCreditGcc
                  ? "Credit GCC"
                  : isCreditOthers
                    ? "Credit Others Customer"
                    : isUnsecured
                      ? "Unsecured Credit"
                      : isDepots
                        ? "Depots"
                        : isVolumes
                          ? "Total"
                          : isTurnover
                            ? "Total Turn Over"
                            : "Total";
        return {
          fontWeight: "bold",
          backgroundColor: _COLORS.groupTotalRow,
          color: "black",
        };
      }
    }

    if (
      params?.data?.sale === _INPUT_SALES_OTHERS.DISCOUNT_PMX ||
      params?.data?.sale === _INPUT_SALES_OTHERS.DISCOUNT_DMX ||
      params?.data?.sale === _INPUT_SALES_OTHERS.UNSECURED_CREDIT
    ) {
      return {
        color: "red",
      };
    }

    if (params?.data?.sale === _INPUT_SALES_OTHERS.TOTAL_EXTERNAL_INTERNAL) {
      return {
        fontWeight: "bold",
        backgroundColor: "#92d050",
        color: "black",
      };
    }

    if (params?.data?.sale === _INPUT_SALES_OTHERS.INTERNAL) {
      return {
        fontWeight: "bold",
        backgroundColor: _COLORS.midGrey,
        color: _COLORS.groupTitleColor,
      };
    }

    if (params?.data?.sale === _INPUT_SALES_OTHERS.BY_PRODUCT_RS) {
      return {
        fontWeight: "bold",
        backgroundColor: _COLORS.lightGrey,
      };
    }
    return null;
  };

  const customAggFunc1 = (params) => {
    const month = parseInt(params.column.colId.replace("months.", ""));
    const lineUnsecured = computedRows?.find(
      (row) => row?.sale === _INPUT_SALES_OTHERS.UNSECURED_CREDIT,
    );
    const valueUnsecured =
      tab === _TAB_INPUT_SALES.VOLUME
        ? lineUnsecured?.months[month]
        : lineUnsecured?.months[month] * parseFloat(lineUnsecured?.credit);

    let total = 0;

    params?.values?.forEach((value, key) => {
      total += key < 4 && value !== undefined ? value : 0;
    });
    // add unsecured credit to external credit total
    if (
      params?.rowNode?.key ===
        _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING &&
      valueUnsecured !== undefined &&
      valueUnsecured !== null
    ) {
      total += valueUnsecured;
    }

    return total;
  };

  const groupTotalRow = (params) => {
    if (
      params.node.allLeafChildren &&
      params.node.allLeafChildren.length === 1
    ) {
      return undefined; // No total if only one line
    }

    return params?.node.key === _INPUT_SALE_REGROUPING.DEPOT_GROUPING ||
      params?.node.key === _INPUT_SALE_REGROUPING.VOLUMES_GROUPING ||
      params?.node.key === _INPUT_SALE_REGROUPING.TURN_GROUPING
      ? "bottom"
      : "top";
  };

  //Internal and external volume
  useEffect(() => {
    if (computedRows.length === 0 || tab !== _TAB_INPUT_SALES.VOLUME) return;

    const processVolume = (type) => {
      const volumeData = computedRows?.find((row) => row?.sale === type);
      const newRef = volumeData?.months;
      const clonedValuesForVolumes = [...valuesForVolumes.current];
      while (clonedValuesForVolumes.length > 0) {
        saveVolume(newRef, clonedValuesForVolumes.shift(), type.toLowerCase());
      }
    };

    processVolume(_INPUT_SALES_OTHERS.EXTERNAL_VOLUME);
    processVolume(_INPUT_SALES_OTHERS.INTERNAL_VOLUME);

    valuesForVolumes.current = [];
  }, [refreshData]);

  const saveVolume = (datasVolumes, valueVolume, option) => {
    if (datasVolumes === undefined) return;

    const dataVolume = datasVolumes[valueVolume.month];
    const id = valueVolume.id;

    if (id !== undefined) {
      // save data
      const payload = {};
      option.includes("internal")
        ? (payload.internalVolume = dataVolume)
        : (payload.externalVolume = dataVolume);
      requestHelper
        .doRequest(
          _ACTIONS.PATCH,
          _ROUTES.IPT_SALE_AGG_MONTHS + "/" + id,
          payload,
        )
        .then((res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            createToast(
              _SWAL_ICONS.success,
              "Input sale agg saved successfully",
            );
          }
        })
        .catch(() => {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        });
    }
  };

  const defaultColDef = {
    cellClassRules: {
      "total-row": (params) =>
        params.data?.sale === _INPUT_SALES_OTHERS.TOTAL_EXTERNAL_INTERNAL,
      "total-header-col": (params) => {
        return params.colDef.field === "total";
      },
      "agg-row": (params) =>
        params.node.aggData !== undefined ||
        params.data.sale === _INPUT_SALES_OTHERS.BY_PRODUCT_RS,
      "discount-row": (params) =>
        params.colDef.field === "credit" &&
        params.data !== undefined &&
        params.data.sale === "Credit discount",
    },
  };

  const excelStyles = defaultExcelStyle();

  /**
   * Memoized configuration object for exporting data to Excel.
   *
   * @constant
   * @type {Object}
   * @property {Function} shouldRowBeSkipped - Determines whether a row should be skipped during export.
   *   @param {Object} params - Parameters for the row.
   *   @param {Object} params.node - The node representing the row.
   *   @param {string} params.node.id - The ID of the row node.
   *   @param {string} params.node.key - The key of the row node.
   *   @returns {boolean} - Returns true if the row should be skipped, false otherwise.
   *
   * @property {Function} processCellCallback - Processes the value of a cell before exporting.
   *   @param {Object} params - Parameters for the cell.
   *   @param {any} params.value - The value of the cell.
   *   @param {Object} params.node - The node representing the row.
   *   @param {Object} [params.node.data] - The data associated with the row node.
   *   @param {string} [params.node.data.type] - The type of the row data.
   *   @param {Object} [params.node.data.months] - Monthly data for the row.
   *   @param {Object} [params.node.data.calculs] - Calculations data for the row.
   *   @param {number} [params.node.data.totalLigne] - The total line value for the row.
   *   @param {Object} params.column - The column information.
   *   @param {string} params.column.colId - The ID of the column.
   *   @returns {any} - Returns the processed cell value.
   */
  const defaultExcelExportParams = useMemo(() => {
    return {
      shouldRowBeSkipped: (params) => {
        return params.node.id === "row-group-type-" + params.node.key; // Prevents this row from being exported
      },
      processCellCallback: (params) => {
        if (params.value === "Total  -> ") {
          params.value = params.node.key;
        }

        if (
          params.node.data !== undefined &&
          params.node.data.totalLigne !== undefined &&
          params.column.colId === "total"
        ) {
          return params.node.data.totalLigne;
        }

        if (
          params.node.data !== undefined &&
          params.node.data.type.includes("vide")
        ) {
          return "";
        }

        if (
          params.column.colId === "credit" &&
          params.node.data !== undefined &&
          params.node.data.type ===
            _INPUT_SALE_REGROUPING.EXTERNAL_CREDIT_GROUPING &&
          tab === _TAB_INPUT_SALES.VALUE
        ) {
          // Calculation of credits for EXTERNAL CREDIT
          const totalVolumes = Object.values(params.node.data.months).reduce(
            (acc, curr) => acc + curr,
            1,
          );
          const totalValues = Object.values(params.node.data.calculs).reduce(
            (acc, curr) => acc + curr,
            0,
          );

          return (totalValues / totalVolumes).toFixed(2);
        }

        return params.value;
      },
    };
  }, []);

  return (
    <DataTable
      rows={computedRows}
      columns={columns?.map((col) => {
        if (col.field.includes("months.")) {
          return { ...col, aggFunc: customAggFunc1 };
        }
        return col;
      })}
      onCellValueChanged={handleCellValueChanged}
      fullHeight={true}
      excelStyles={excelStyles}
      gridRef={gridRef}
      agGridProps={{
        defaultExcelExportParams: defaultExcelExportParams,
        groupTotalRow: groupTotalRow,
        groupDisplayType: "groupRows",
        groupHideParentOfSingleChild: true,
        cellSelection: true,
        tooltipShowDelay: 0,
        enableBrowserTooltips: true,
        gridOptions: {
          enableBrowserTooltips: true,
          defaultColDef: defaultColDef,
          suppressAggFuncInHeader: true,
          getRowStyle: getRowStyle,
          domLayout: "normal",
          suppressHorizontalScroll: false,
          suppressPasteSingleValueOperation: true,
          getRowNodeId:
            tab === _TAB_INPUT_SALES.VOLUME ? (data) => data.id : undefined,
          getRowId:
            tab !== _TAB_INPUT_SALES.VOLUME
              ? (params) => params.data.sale + params.data?.type
              : undefined,
        },
        groupDefaultExpanded: -1,
      }}
    />
  );
};

export default TableInputSaleAggVolume;
